import React from 'react';
import styled, { useTheme } from 'styled-components';

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
  gap: 10px;

  p {
    border: none;
    cursor: pointer;
    font-size: 1rem;
    line-height: 24px;
    margin: 0 5px;
  }

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const IconContainerFirst = styled.div<{ disabled?: boolean }>`
  display: flex;
  padding: 5px 10px 5px 15px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.grayColor : '#91C6B2'};
  color: ${(props) => props.theme.colors.whiteColor};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  border-radius: 15px 0 0 15px; /* 왼쪽 모서리 둥글게 */

  &:hover {
    background-color: ${(props) =>
      props.disabled
        ? props.theme.colors.grayColor
        : props.theme.colors.hoverColor};
  }

  @media (max-width: 768px) {
    padding: 3px 5px 3px 10px;
    span {
      border-radius: 10px 0 0 10px; /* 왼쪽 모서리 둥글게 */
      font-size: 12px;
    }
  }
`;

const IconContainerLast = styled.div<{ disabled?: boolean }>`
  display: flex;
  padding: 5px 15px 5px 10px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.grayColor : '#91C6B2'};
  color: ${(props) => props.theme.colors.whiteColor};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  border-radius: 0px 15px 15px 0px; /* 오른쪽 모서리 둥글게 */

  &:hover {
    background-color: ${(props) =>
      props.disabled
        ? props.theme.colors.grayColor
        : props.theme.colors.hoverColor};
  }

  @media (max-width: 768px) {
    padding: 3px 10px 3px 5px;
    span {
      border-radius: 10px 0 0 10px; /* 왼쪽 모서리 둥글게 */
      font-size: 12px;
    }
  }
`;

const IconContainer2 = styled.div<{ disabled?: boolean }>`
  display: flex;
  padding: 5px 10px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.disabled
      ? props.theme.colors.grayColor
      : props.theme.colors.sub3Color};
  color: ${(props) => props.theme.colors.whiteColor};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background-color: ${(props) =>
      props.disabled
        ? props.theme.colors.grayColor
        : props.theme.colors.hoverColor};
  }

  @media (max-width: 768px) {
    padding: 5px;
    span {
      font-size: 12px;
    }
  }
`;

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const theme = useTheme();
  const displayPage = currentPage + 1; // displayPage를 currentPage + 1로 설정하여 1부터 시작

  if (totalPages <= 0) {
    return null;
  }

  const handleFirstPage = () => {
    if (displayPage > 1) onPageChange(0);
  };
  const handlePrevPage = () => {
    if (displayPage > 1) onPageChange(currentPage - 1);
  };
  const handleNextPage = () => {
    if (displayPage < totalPages) onPageChange(currentPage + 1);
  };
  const handleLastPage = () => {
    if (displayPage < totalPages) onPageChange(totalPages - 1);
  };
  const handlePageClick = (page: number) => onPageChange(page - 1);

  const renderPageNumbers = () => {
    const pages = [];
    let startPage = Math.max(1, displayPage - 2);
    let endPage = Math.min(totalPages, displayPage + 2);

    if (endPage - startPage < 4) {
      if (displayPage < 3) {
        endPage = Math.min(5, totalPages);
      } else {
        startPage = Math.max(totalPages - 4, 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <p
          key={i}
          onClick={() => handlePageClick(i)}
          style={{
            fontWeight: displayPage === i ? 'bold' : 'normal',
            color:
              displayPage === i
                ? theme.colors.mainColor
                : theme.colors.textColor,
          }}
        >
          {i}
        </p>
      );
    }
    return pages;
  };

  return (
    <PaginationContainer>
      <IconContainerFirst
        onClick={handleFirstPage}
        disabled={displayPage === 1}
      >
        <span>First</span>
      </IconContainerFirst>
      <IconContainer2 onClick={handlePrevPage} disabled={displayPage === 1}>
        <span>Prev</span>
      </IconContainer2>
      {renderPageNumbers()}
      <IconContainer2
        onClick={handleNextPage}
        disabled={displayPage === totalPages}
      >
        <span>Next</span>
      </IconContainer2>
      <IconContainerLast
        onClick={handleLastPage}
        disabled={displayPage === totalPages}
      >
        <span>Last</span>
      </IconContainerLast>
    </PaginationContainer>
  );
};

export default Pagination;
